import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ConnectorAuditEventResponse } from "@/api";
import { adminConnectorAuditOptions } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";

const KNOWN = [
  "added",
  "changed",
  "deleted",
  "shared",
  "secret_set",
  "secret_cleared",
  "personal_token_set",
  "personal_token_cleared",
  "tool_changed",
  "tool_run",
  "disabled_by_admin",
  "enabled_by_admin",
] as const;
type Known = (typeof KNOWN)[number];
const isKnown = (action: string): action is Known => (KNOWN as readonly string[]).includes(action);

function useDescribe() {
  const { t } = useTranslation("adminIntegrations");
  return (e: ConnectorAuditEventResponse) => {
    const values = {
      actor: e.actorName ?? t("connectors.someone"),
      name: e.connectorName,
      tool: String((e.detail as { tool?: string }).tool ?? ""),
      action: e.action,
    };
    return isKnown(e.action) ? t(`connectors.actions.${e.action}`, values) : t("connectors.actions.other", values);
  };
}

/** Recent connector activity (the audit trail, D16). */
export function ConnectorActivitySection() {
  const { t } = useTranslation("adminIntegrations");
  const describe = useDescribe();
  const events = useQuery({ ...adminConnectorAuditOptions({ query: { limit: 25 } }), select: (r) => r.data });
  const rows = events.data ?? [];
  return (
    <PageSection title={t("connectors.activityTitle")} description={t("connectors.activityBody")}>
      {events.isSuccess && rows.length === 0 && (
        <Text variant="body-sm" tone="tertiary">
          {t("connectors.noActivity")}
        </Text>
      )}
      {rows.length > 0 && (
        <ul aria-label={t("connectors.activityLabel")} className="flex flex-col divide-y divide-border-subtle">
          {rows.map((e) => (
            <li key={e.id} className="flex flex-col gap-0.5 py-2 md:flex-row md:items-center md:justify-between">
              <Text as="span" variant="body-sm">
                {describe(e)}
              </Text>
              <Text as="span" variant="caption" tone="tertiary">
                {formatRelativeTime(e.at)}
              </Text>
            </li>
          ))}
        </ul>
      )}
    </PageSection>
  );
}
